/**
 * Calendar files (.ics, RFC 5545) for the dates the app already knows: debt
 * instalments, an insurance renewal, the next vehicle inspection.
 *
 * Every event is all-day and carries a stable UID, so importing a fresh file
 * updates the events the last one made instead of adding a second set. The
 * file is built on the device; nothing is sent anywhere.
 */

const PRODID = '-//Liquidity Raiders//Portfolio Tracker//PL';

/** Text values escape backslashes, semicolons, commas and line breaks. */
const text = value => String(value ?? '')
  .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

const stamp = date => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const day = iso => iso.replace(/-/g, '');

/*
 * Lines longer than 75 octets are folded: CRLF and a space. Counted in UTF-8
 * bytes -- Polish letters take two -- and never cut through a character.
 */
function fold(line) {
  const bytes = ch => (ch.codePointAt(0) < 0x80 ? 1 : ch.codePointAt(0) < 0x800 ? 2 : ch.codePointAt(0) < 0x10000 ? 3 : 4);
  const out = [];
  let current = '';
  let size = 0;
  for (const ch of line) {
    const n = bytes(ch);
    if (size + n > (out.length ? 74 : 75)) {
      out.push(current);
      current = '';
      size = 0;
    }
    current += ch;
    size += n;
  }
  out.push(current);
  return out.join('\r\n ');
}

/**
 * The day of the month a payment falls on, as a rule that survives short
 * months: the 31st becomes the last day of a 30-day month and of February,
 * where a plain BYMONTHDAY=31 would skip those months altogether.
 */
export function monthlyRule(payDay, count = null) {
  const d = Math.min(31, Math.max(1, Math.round(payDay)));
  const days = d <= 28 ? String(d) : Array.from({ length: d - 27 }, (_, i) => 28 + i).join(',');
  return 'FREQ=MONTHLY;BYMONTHDAY=' + days + (d > 28 ? ';BYSETPOS=-1' : '') + (count > 0 ? ';COUNT=' + Math.round(count) : '');
}

/** The first payment day on or after `from` (YYYY-MM-DD), clamped to the month's length. */
export function nextPayDate(payDay, from) {
  const [y, m, dd] = from.split('-').map(Number);
  const at = (year, month) => {
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return new Date(Date.UTC(year, month - 1, Math.min(payDay, last))).toISOString().slice(0, 10);
  };
  const here = at(y, m);
  return Number(here.slice(8)) >= dd ? here : at(m === 12 ? y + 1 : y, m === 12 ? 1 : m + 1);
}

/**
 * @param events [{ uid, date: 'YYYY-MM-DD', summary, description?, rrule?, alarm? }]
 *               alarm is an ISO duration before the day starts, e.g. '-P14D'.
 */
export function buildIcs(events, { now = new Date(), name = 'Portfolio Tracker' } = {}) {
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:' + PRODID, 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'X-WR-CALNAME:' + text(name)
  ];
  for (const event of events) {
    const next = new Date(Date.parse(event.date) + 864e5).toISOString().slice(0, 10);
    lines.push(
      'BEGIN:VEVENT',
      'UID:' + event.uid + '@liquidityraiders.pl',
      'DTSTAMP:' + stamp(now),
      'DTSTART;VALUE=DATE:' + day(event.date),
      'DTEND;VALUE=DATE:' + day(next),
      'SUMMARY:' + text(event.summary),
      ...(event.description ? ['DESCRIPTION:' + text(event.description)] : []),
      ...(event.rrule ? ['RRULE:' + event.rrule] : []),
      'TRANSP:TRANSPARENT'
    );
    if (event.alarm) {
      lines.push('BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + text(event.summary), 'TRIGGER:' + event.alarm, 'END:VALARM');
    }
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(fold).join('\r\n') + '\r\n';
}
